// TaskFormBody — the Create / Edit Task form fields, extracted from Tasks.js
// so keystrokes in the form don't force the parent (with 1500+ task rows) to
// re-render. All form state is LOCAL. Only fires callbacks on submit.
//
// Parent contract:
//   <TaskFormBody
//     editingTask={editingTask}       // null when creating
//     services={services}          // Work Planner service catalogue [{key,label,color}]
//     teamMembers={teamMembers}
//     clients={clients}
//     initialService={selectedService}          // "" or a service key — pre-fills a new task's service
//     initialDueDate={quickCreateDate}          // "yyyy-MM-dd'T'HH:mm" or "" — pre-fills a new task's due date
//     initialClientId={preselectedClientId}     // "" or a client_id — pre-fills a new task's client
//     statusConfig={STATUS_CONFIG}
//     priorityConfig={PRIORITY_CONFIG}
//     healthConfig={HEALTH_CONFIG}
//     roleColors={ROLE_COLORS}
//     roleLabels={ROLE_LABELS}
//     onSubmit={(formData) => Promise<void>}   // parent handles the POST/PUT
//     submitting={boolean}
//   />

import { memo, useEffect, useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { format } from "date-fns";
import { Briefcase, Flame } from "lucide-react";

const emptyForm = (initialService = "", initialDueDate = "", initialClientId = "") => ({
  title: "",
  description: "",
  service: initialService || "",
  client_id: initialClientId || "",
  due_date: initialDueDate || "",
  priority: "medium",
  health_status: "on_track",
  status: "to_do",
  is_urgent: false,
  assigned_users: [],
});

const formFromTask = (task) => ({
  title: task.title || "",
  description: task.description || "",
  service: task.service || "",
  project_id: task.project_id || "",
  client_id: task.client_id || "",
  due_date: task.due_date ? format(new Date(task.due_date), "yyyy-MM-dd'T'HH:mm") : "",
  priority: task.priority || "medium",
  health_status: task.health_status || "on_track",
  status: task.status || "to_do",
  is_urgent: task.is_urgent || false,
  assigned_users: task.assigned_users || [],
});

function TaskFormBodyImpl({
  editingTask, services = [], teamMembers = [], clients = [],
  initialService = "", initialDueDate = "", initialClientId = "",
  statusConfig = {}, priorityConfig = {}, healthConfig = {},
  roleColors = {}, roleLabels = {},
  onSubmit, submitting = false,
}) {
  const [form, setForm] = useState(() =>
    editingTask ? formFromTask(editingTask) : emptyForm(initialService, initialDueDate, initialClientId)
  );
  const [clientSearch, setClientSearch] = useState("");

  // Reset form whenever the target task changes (or dialog reopens for a new task)
  useEffect(() => {
    setForm(editingTask ? formFromTask(editingTask) : emptyForm(initialService, initialDueDate, initialClientId));
    setClientSearch("");
  }, [editingTask, initialService, initialDueDate, initialClientId]);

  const filteredClients = useMemo(() => {
    const q = clientSearch.trim().toLowerCase();
    if (!q) return clients;
    return clients.filter(c => (c.client_name || "").toLowerCase().includes(q));
  }, [clients, clientSearch]);

  const set = (patch) => setForm(f => ({ ...f, ...patch }));

  const handleSave = () => {
    if (!form.title.trim()) { toast.error("Task title is required"); return; }
    onSubmit && onSubmit(form);
  };

  return (
    <div className="space-y-4 mt-4 max-h-[70vh] overflow-y-auto pr-2">
      <div>
        <Label>Task Title *</Label>
        <Input
          value={form.title}
          onChange={(e) => set({ title: e.target.value })}
          placeholder="Enter task title"
          className="mt-1"
          data-testid="task-title-input"
          autoFocus
        />
      </div>

      <div>
        <Label>Description</Label>
        <Textarea
          value={form.description}
          onChange={(e) => set({ description: e.target.value })}
          placeholder="Task description..."
          className="mt-1"
          rows={3}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Service</Label>
          <Select value={form.service || "none"} onValueChange={(v) => set({ service: v === "none" ? "" : v })}>
            <SelectTrigger className="mt-1" data-testid="task-service-select"><SelectValue placeholder="Select service" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">No Service</SelectItem>
              {services.map(svc => (
                <SelectItem key={svc.key} value={svc.key}>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: svc.color }} />
                    {svc.label}
                  </div>
                </SelectItem>
              ))}
              {form.service && !services.some(svc => svc.key === form.service) && (
                <SelectItem value={form.service}>{form.service} (archived)</SelectItem>
              )}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Client Name</Label>
          <Select value={form.client_id || "none"} onValueChange={(v) => set({ client_id: v === "none" ? "" : v })}>
            <SelectTrigger className="mt-1" data-testid="task-client-select"><SelectValue placeholder="Select client" /></SelectTrigger>
            <SelectContent>
              <div className="px-2 py-1.5 sticky top-0 bg-white dark:bg-slate-900">
                <Input
                  placeholder="Search clients..."
                  value={clientSearch}
                  onChange={(e) => setClientSearch(e.target.value)}
                  className="h-8"
                  data-testid="client-search-in-dropdown"
                />
              </div>
              <SelectItem value="none">No Client</SelectItem>
              {filteredClients.map(client => (
                <SelectItem key={client.client_id} value={client.client_id}>
                  <div className="flex items-center gap-2">
                    <Briefcase className="w-3 h-3 text-slate-400" />
                    {client.client_name}
                  </div>
                </SelectItem>
              ))}
              {filteredClients.length === 0 && clientSearch && (
                <div className="px-2 py-2 text-sm text-slate-500">No clients found</div>
              )}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <Label>Status</Label>
        <Select value={form.status} onValueChange={(v) => set({ status: v })}>
          <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
          <SelectContent>
            {Object.entries(statusConfig).filter(([key]) => !["in_progress", "review"].includes(key)).map(([key, cfg]) => (
              <SelectItem key={key} value={key}>{cfg.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className={`grid gap-4 ${Object.keys(healthConfig).length > 0 ? "grid-cols-2" : "grid-cols-1"}`}>
        <div>
          <Label>Priority</Label>
          <Select value={form.priority} onValueChange={(v) => set({ priority: v })}>
            <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(priorityConfig).map(([key, cfg]) => (
                <SelectItem key={key} value={key}>
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${cfg.dotColor}`} />
                    {cfg.label}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {/* Health Status only applies to the Tasks module's own workflow —
            omitted when the caller doesn't pass a healthConfig (e.g. the
            Client Work Planner's scheduler dialog), rather than rendering an
            empty, option-less dropdown. */}
        {Object.keys(healthConfig).length > 0 && (
        <div>
          <Label>Health Status</Label>
          <Select value={form.health_status} onValueChange={(v) => set({ health_status: v })}>
            <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(healthConfig).map(([key, cfg]) => (
                <SelectItem key={key} value={key}>
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${cfg.dotColor}`} />
                    {cfg.label}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        )}
      </div>

      <div className={`flex items-center justify-between p-3 rounded-xl border ${form.is_urgent ? "bg-red-50 border-red-200" : "bg-slate-50 border-slate-200"}`}>
        <div className="flex items-center gap-2">
          <Flame className={`w-5 h-5 ${form.is_urgent ? "text-red-500" : "text-slate-400"}`} />
          <div>
            <p className={`text-sm font-medium ${form.is_urgent ? "text-red-700" : "text-slate-700"}`}>Mark as Urgent</p>
            <p className="text-xs text-slate-400">Urgent tasks are highlighted and prioritized</p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => set({
            is_urgent: !form.is_urgent,
            priority: !form.is_urgent ? "urgent" : (form.priority === "urgent" ? "high" : form.priority),
          })}
          className={`relative w-11 h-6 rounded-full transition-colors ${form.is_urgent ? "bg-red-500" : "bg-slate-300"}`}
        >
          <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${form.is_urgent ? "translate-x-5" : ""}`} />
        </button>
      </div>

      <div>
        <Label>Due Date</Label>
        <Input
          type="datetime-local"
          value={form.due_date}
          onChange={(e) => set({ due_date: e.target.value })}
          className="mt-1"
        />
      </div>

      <div>
        <Label>Assign To</Label>
        <div className="mt-2 space-y-2 max-h-40 overflow-y-auto border rounded-lg p-2">
          {teamMembers.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-2">No team members available</p>
          ) : (
            teamMembers.map(member => (
              <label key={member.user_id} className="flex items-center gap-2 p-2 hover:bg-slate-50 dark:hover:bg-slate-700 rounded cursor-pointer" data-testid={`assign-user-${member.user_id}`}>
                <Checkbox
                  checked={form.assigned_users.includes(member.user_id)}
                  onCheckedChange={(checked) => {
                    if (checked) set({ assigned_users: [...form.assigned_users, member.user_id] });
                    else         set({ assigned_users: form.assigned_users.filter(id => id !== member.user_id) });
                  }}
                />
                <span className="text-sm">{member.name}</span>
                <Badge className={`text-[10px] ml-auto ${roleColors[member.role] || roleColors.employee}`}>
                  {roleLabels[member.role] || member.role}
                </Badge>
              </label>
            ))
          )}
        </div>
      </div>

      <Button
        onClick={handleSave}
        disabled={submitting}
        className="w-full bg-indigo-600 hover:bg-indigo-700 text-white rounded-full"
        data-testid="save-task-button"
      >
        {submitting ? "Saving…" : editingTask ? "Update Task" : "Create Task"}
      </Button>
    </div>
  );
}

export default memo(TaskFormBodyImpl);
